import React, { useState, useEffect } from 'react';
import { useOrgUser } from '../context/OrgUserProvider';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/AutoStoriesOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CourseFilters from '../components/CourseFilters';
import CourseSortBy from '../components/CourseSortBy';
import PaginationRounded from '../components/Pagination';
import LockIcon from '@mui/icons-material/Lock';

const S3_BASE_URL = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/SoftwareCourses';
const COURSES_MANIFEST_URL = `${S3_BASE_URL}/course-manifest.json`;

const AdvancedCourses = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('duration-asc');
  const page = parseInt(searchParams.get('page')) || 1;
  const coursesPerPage = 6;
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbnails, setThumbnails] = useState({});
  const { orgUser } = useOrgUser();
  const { loginWithRedirect } = useAuth0();

  // Check if user is logged in by checking if orgUser has any properties
  const isLoggedIn = orgUser && Object.keys(orgUser).length > 0;

  const fetchOptions = {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    },
    credentials: 'same-origin',
    referrerPolicy: 'no-referrer'
  };

  // Filter options
  const levels = ['Advanced'];
  const durations = ['0-30 mins', '30-60 mins', '60+ mins'];
  const lessonsNo = ['1-5 lessons', '6-10 lessons', '10+ lessons'];

  // Filter states
  const [selectedLevels, setSelectedLevels] = useState(['Advanced']);
  const [selectedLessonsNo, setSelectedLessonsNo] = useState([]);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add cache-busting parameter to prevent browser caching
      const cacheBuster = `?v=${Date.now()}`;

      // First fetch the course manifest
      const manifestResponse = await fetch(COURSES_MANIFEST_URL + cacheBuster, fetchOptions);

      if (!manifestResponse.ok) {
        throw new Error(`Failed to fetch course manifest: ${manifestResponse.status}`);
      }

      const manifest = await manifestResponse.json();
      const publishedCourses = manifest.courses.filter(course => !course.isDeleted);

      // Fetch course info for each published course
      const coursePromises = publishedCourses.map(async (course) => {
        try {
          const courseInfoUrl = `${S3_BASE_URL}/${course.id}/course-info.json${cacheBuster}`;

          const courseInfoResponse = await fetch(courseInfoUrl, fetchOptions);
          if (!courseInfoResponse.ok) {
            console.warn(`Failed to fetch course info for ${course.slug}: ${courseInfoResponse.status}`);
            return null;
          }

          const courseInfo = await courseInfoResponse.json();
          
          // Only include Advanced level published courses
          if (courseInfo.level === 'Advanced' && (courseInfo.status === 'Published' || courseInfo.Status === 'Published')) {
            const normalizedCourse = {
              Id: courseInfo.id || courseInfo.Id || course.slug,
              Title: courseInfo.title || courseInfo.Title,
              Level: courseInfo.level || courseInfo.Level,
              Slug: courseInfo.slug || courseInfo.Slug || course.slug,
              Duration: formatDurationFromValue(courseInfo.duration || courseInfo.Duration),
              LessonsCount: courseInfo.noOflessons || courseInfo.lessonsCount || courseInfo.LessonsCount || 0,
              Status: courseInfo.status || courseInfo.Status,
              ImageKey: courseInfo.thumbnail || courseInfo.ImageKey || '',
              Category: courseInfo.category || courseInfo.Category || 'Uncategorized',
              // Store the thumbnail URL for public access
              ThumbnailUrl: courseInfo.thumbnail ?
                `${S3_BASE_URL}/${course.id}/${courseInfo.thumbnail}` :
                null
            };
            return normalizedCourse;
          }
          return null; // Skip non-Advanced courses
        } catch (err) {
          console.error(`Error fetching course info for ${course.slug}:`, err);
          return null;
        }
      });

      const allCourses = (await Promise.all(coursePromises)).filter(course => course !== null);
      setCourses(allCourses);

      // Preload thumbnails
      await preloadThumbnails(allCourses);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const preloadThumbnails = async (courses) => {
    const thumbnailMap = {};

    const preloadPromises = courses.map(async (course) => {
      if (course.ThumbnailUrl) {
        try {
          // Add cache busting to thumbnail URLs too
          const cacheBuster = `?v=${Date.now()}`;
          const thumbnailUrl = course.ThumbnailUrl + cacheBuster;

          // Create an image object to preload
          const img = new Image();
          img.src = thumbnailUrl;

          // Wait for image to load or fail
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });

          thumbnailMap[course.Slug] = course.ThumbnailUrl;
        } catch (err) {
          console.warn(`Failed to preload thumbnail for ${course.Slug}:`, err);
          thumbnailMap[course.Slug] = null;
        }
      } else {
        thumbnailMap[course.Slug] = null;
      }
    });

    await Promise.all(preloadPromises);
    setThumbnails(thumbnailMap);
  };

  // Function to format duration from various possible formats
  const formatDurationFromValue = (duration) => {
    if (typeof duration === 'number') {
      // Convert decimal hours to "Xh Ym" format
      const hours = Math.floor(duration);
      const minutes = Math.round((duration - hours) * 60);

      if (hours === 0) {
        return `${minutes}m`;
      } else if (minutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${minutes}m`;
      }
    } else if (typeof duration === 'string') {
      // If it's already a string, return as is
      return duration;
    } else {
      // Default fallback
      return '0h 0m';
    }
  };

  // Filter handlers
  const handleLevelChange = (Level) => {
    if (selectedLevels.includes(Level)) {
      setSelectedLevels(selectedLevels.filter(l => l !== Level));
    } else {
      setSelectedLevels([...selectedLevels, Level]);
    }
    setSearchParams({ page: 1 });
  };

  const handleDurationChange = (Duration) => {
    if (selectedDurations.includes(Duration)) {
      setSelectedDurations(selectedDurations.filter(d => d !== Duration));
    } else {
      setSelectedDurations([...selectedDurations, Duration]);
    }
    setSearchParams({ page: 1 });
  };

  const handleLessonsNoChange = (lessonRange) => {
    if (selectedLessonsNo.includes(lessonRange)) {
      setSelectedLessonsNo(selectedLessonsNo.filter(l => l !== lessonRange));
    } else {
      setSelectedLessonsNo([...selectedLessonsNo, lessonRange]);
    }
    setSearchParams({ page: 1 });
  };

  const handleCategoryChange = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
    setSearchParams({ page: 1 });
  };
  
  const resetFilters = () => {
    setSelectedLevels(['Advanced']);
    setSelectedDurations([]);
    setSelectedLessonsNo([]);
    setSelectedCategories([]);
    setSearchQuery('');
    setSearchParams({ page: 1 });
  };

  const resetSortBy = () => {
    setSortBy('duration-asc');
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Basic':
        return '#26aa99';
      case 'Intermediate':
        return '#332872';
      case 'Advanced':
        return '#e33e1d';
      default:
        return '#9E9E9E';
    }
  };

  // Apply filters
  const filteredCourses = courses.filter(course => {
    // Search filter
    const matchesSearch = course.Title.toLowerCase().includes(searchQuery.toLowerCase());

    // Level filter - only show Advanced courses
    const matchesLevel = selectedLevels.length === 0 || selectedLevels.includes(course.Level);

    // Duration filter
    const matchesDuration = selectedDurations.length === 0 ||
      selectedDurations.some(d => {
        // Convert duration string to total minutes
        const durationStr = course.Duration;
        let totalMinutes = 0;

        if (durationStr.includes('h') && durationStr.includes('m')) {
          // Format like "0h 15m"
          const [hoursStr, minutesStr] = durationStr.split('h ');
          const hours = parseInt(hoursStr) || 0;
          const minutes = parseInt(minutesStr) || 0;
          totalMinutes = (hours * 60) + minutes;
        } else if (durationStr.includes('h')) {
          // Format like "1h"
          const hours = parseInt(durationStr) || 0;
          totalMinutes = hours * 60;
        } else if (durationStr.includes('m')) {
          // Format like "15m"
          totalMinutes = parseInt(durationStr) || 0;
        }

        if (d === '0-30 mins') return totalMinutes <= 30;
        if (d === '30-60 mins') return totalMinutes >= 30 && totalMinutes <= 60;
        if (d === '60+ mins') return totalMinutes >= 60;
        return true;
      });

    // Lesson number filter
    const matchesLessonsNo = selectedLessonsNo.length === 0 ||
      selectedLessonsNo.some(range => {
        const lessonCount = course.LessonsCount;
        if (range === '1-5 lessons') return lessonCount >= 1 && lessonCount <= 5;
        if (range === '6-10 lessons') return lessonCount >= 6 && lessonCount <= 10;
        if (range === '10+ lessons') return lessonCount > 10;
        return true;
      });

    // Category filter
    const matchesCategory = selectedCategories.length === 0 ||
      selectedCategories.includes(course.Category);

    return matchesSearch && matchesLevel && matchesDuration && matchesLessonsNo && matchesCategory;
  });

  // Helper function for sorting - convert duration to minutes
  const convertDurationToMinutes = (durationStr) => {
    let totalMinutes = 0;

    if (durationStr.includes('h') && durationStr.includes('m')) {
      // Format like "0h 15m"
      const [hoursStr, minutesStr] = durationStr.split('h ');
      const hours = parseInt(hoursStr) || 0;
      const minutes = parseInt(minutesStr) || 0;
      totalMinutes = (hours * 60) + minutes;
    } else if (durationStr.includes('h')) {
      // Format like "1h"
      const hours = parseInt(durationStr) || 0;
      totalMinutes = hours * 60;
    } else if (durationStr.includes('m')) {
      // Format like "15m"
      totalMinutes = parseInt(durationStr) || 0;
    }

    return totalMinutes;
  };

  // Apply sorting
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (sortBy === 'duration-asc') {
      const aMinutes = convertDurationToMinutes(a.Duration);
      const bMinutes = convertDurationToMinutes(b.Duration);
      return aMinutes - bMinutes;
    } else if (sortBy === 'duration-desc') {
      const aMinutes = convertDurationToMinutes(a.Duration);
      const bMinutes = convertDurationToMinutes(b.Duration);
      return bMinutes - aMinutes;
    } else if (sortBy === 'lessons-asc') {
      return a.LessonsCount - b.LessonsCount;
    } else if (sortBy === 'lessons-desc') {
      return b.LessonsCount - a.LessonsCount;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedCourses.length / coursesPerPage);
  const paginatedCourses = sortedCourses.slice(
    (page - 1) * coursesPerPage,
    page * coursesPerPage
  )

  const handlePageChange = (event, value) => {
    setSearchParams({ page: value });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDurationForDisplay = (durationStr) => {
    // If it's already in a display format, return as is
    if (durationStr.includes('h') || durationStr.includes('m')) {
      return durationStr;
    }

    // Otherwise, assume it's a number and format it
    const decimalHours = parseFloat(durationStr);
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  const handleLogin = () => {
    loginWithRedirect();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading courses: {error}</Alert>
      </Box>
    );
  }

  // Get unique categories from courses
  const categories = [...new Set(courses.map(course => course.Category))].filter(Boolean);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Hero Banner */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: '200px'
      }}>
        <Box
          component="img"
          src={`https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/AdvancedBanner.png`}
          alt="Qallix Academy Banner"
          sx={{
            width: '100%',
            height: '200px',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#fff',
            fontWeight: 'bold',
            textAlign: 'center',
            textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
            fontSize: '2rem',
            fontFamily: "'Satoshi', sans-serif",
          }}
        >
          STAY AHEAD OF THE GAME
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{
        backgroundColor: '#F8F8F8',
        width: '100%',
        pt: 4
      }}>
        <Container maxWidth="xl">
          {/* Header Section */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 0 }
          }}>
            <Typography variant="h4" sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: '50px',
              fontWeight: 'bold'
            }}>
              Advanced <Box component="span" sx={{ color: '#7F59EB' }}>Courses</Box>
            </Typography>
            <TextField
              size="small"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchParams({ page: 1 });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '9999px',
                  backgroundColor: '#fff',
                  fontFamily: "'Satoshi', sans-serif",
                }
              }}
              sx={{
                width: { xs: '100%', md: 300 }
              }}
            />
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3
          }}>
            {/* Filters Section */}
            <Box sx={{
              width: { xs: '100%', md: '20%' },
              marginBottom: { md: '130px' }
            }}>
              <CourseFilters
                levels={levels}
                durations={durations}
                lessonsNo={lessonsNo}
                categories={categories}
                selectedLevels={selectedLevels}
                selectedDurations={selectedDurations}
                selectedLessonsNo={selectedLessonsNo}
                selectedCategories={selectedCategories}
                handleLevelChange={handleLevelChange}
                handleDurationChange={handleDurationChange}
                handleLessonsNoChange={handleLessonsNoChange}
                handleCategoryChange={handleCategoryChange}
                resetFilters={resetFilters}
                getLevelColor={getLevelColor}
                showLevelFilter={false}
              />
              <CourseSortBy sortBy={sortBy} setSortBy={setSortBy} resetSortBy={resetSortBy} />
            </Box>
            {/* Courses Grid Section */}
            <Box sx={{
              width: { xs: '100%', md: '80%' },
              mb: 9
            }}>
              {sortedCourses.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="textSecondary" fontFamily="'Satoshi', sans-serif">
                    No Advanced courses found matching your filters.
                  </Typography>
                  <Button onClick={resetFilters} variant="contained" sx={{ mt: 2, fontFamily: "'Satoshi', sans-serif", backgroundColor: '#7F59EB' }}>
                    Reset Filters
                  </Button>
                </Box>
              ) : (
                <>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)',
                      xl: 'repeat(3, 1fr)'
                    },
                    gap: 3,
                    pb: 3
                  }}>
                    {paginatedCourses.map((course) => (
                      <Box key={`${course.Id}-${course.Slug}`} sx={{ position: 'relative' }}>
                        {!isLoggedIn && (
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            zIndex: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '12px',
                            flexDirection: 'column',
                            gap: 1
                          }}>
                            <LockIcon sx={{ fontSize: 40, color: '#332872' }} />
                            <Button
                              variant="contained"
                              onClick={handleLogin}
                              sx={{
                                bgcolor: '#332872',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            >
                              Log In to Access
                            </Button>
                          </Box>
                        )}

                        <Link to={`/courses/${course.Slug}`} style={{ textDecoration: 'none' }}>
                          <Card sx={{
                            borderRadius: 3,
                            boxShadow: 0,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: isLoggedIn ? 'translateY(-4px)' : 'none',
                              boxShadow: isLoggedIn ? 2 : 0,
                              cursor: isLoggedIn ? 'pointer' : 'default'
                            }
                          }}>
                            <Box sx={{ px: 2, pt: 2, position: 'relative' }}>
                              {/* Category Chip positioned on top right of image */}
                              <Box sx={{
                                position: 'absolute',
                                top: 25,
                                right: 25,
                                zIndex: 1
                              }}>
                                <Chip
                                  label={course.Category}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#000000ff',
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    borderRadius: '15px',
                                    px: 0.5,
                                    py: 1.5,
                                    fontFamily: "'Satoshi', sans-serif",
                                    opacity: !isLoggedIn ? 0.7 : 1
                                  }}
                                />
                              </Box>

                              <CardMedia
                                component="img"
                                image={thumbnails[course.Slug] || course.ImageKey}
                                alt={course.Title}
                                sx={{
                                  borderRadius: 2,
                                  width: '100%',
                                  height: '200px',
                                  objectFit: 'cover',
                                  filter: !isLoggedIn ? 'grayscale(100%)' : 'none'
                                }}
                              />
                              <CardContent sx={{ p: 0, flexGrow: 1 }}>
                                <Typography
                                  variant="subtitle1"
                                  gutterBottom
                                  sx={{
                                    marginTop: '10px',
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    fontFamily: "'Satoshi', sans-serif",
                                    color: !isLoggedIn ? 'rgba(0,0,0,0.5)' : 'inherit'
                                  }}
                                >
                                  {course.Title}
                                </Typography>

                                <Box sx={{
                                  display: 'flex',
                                  gap: 1,
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  mt: 1
                                }}>
                                  <Chip
                                    label={course.Level}
                                    size="small"
                                    sx={{
                                      backgroundColor: getLevelColor(course.Level),
                                      color: '#ffffff',
                                      fontWeight: 'bold',
                                      borderRadius: '15px',
                                      px: 0.5,
                                      py: 1.5,
                                      fontFamily: "'Satoshi', sans-serif",
                                      opacity: !isLoggedIn ? 0.7 : 1
                                    }}
                                  />
                                  <Chip
                                    icon={<MenuBookIcon fontSize="small" />}
                                    label={`${course.LessonsCount} lessons`}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#FFFFFF',
                                      color: !isLoggedIn ? 'rgba(0,0,0,0.5)' : '#000000',
                                      fontWeight: 'bold',
                                      borderRadius: '15px',
                                      border: '1px solid #D1D5DB',
                                      px: 1,
                                      py: 1.5,
                                      fontFamily: "'Satoshi', sans-serif",
                                    }}
                                  />
                                  <Chip
                                    icon={<TimerOutlinedIcon fontSize="small" />}
                                    label={formatDurationForDisplay(course.Duration)}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#FFFFFF',
                                      color: !isLoggedIn ? 'rgba(0,0,0,0.5)' : '#000000',
                                      fontWeight: 'bold',
                                      borderRadius: '15px',
                                      border: '1px solid #D1D5DB',
                                      px: 1,
                                      py: 1.5,
                                      fontFamily: "'Satoshi', sans-serif",
                                    }}
                                  />
                                </Box>
                              </CardContent>
                            </Box>
                          </Card>
                        </Link>
                      </Box>
                    ))}
                  </Box>

                  {/* Pagination */}
                  {sortedCourses.length > coursesPerPage && (
                    <PaginationRounded
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                    />
                  )}
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AdvancedCourses;